import React from 'react';
import { Filter, Flame } from 'lucide-react';

const TAG_META = {
  GF:     { label: 'Gluten-Free',         badge: 'GF', badgeBg: '#E8B945', badgeFg: '#2B1810' },
  VG:     { label: 'Vegan/Vegetarian',    badge: 'VG', badgeBg: '#E8B945', badgeFg: '#2B1810' },
  V:      { label: 'Vegetarian',          badge: 'V',  badgeBg: '#E8B945', badgeFg: '#2B1810' },
  N:      { label: 'Contains Nuts',       badge: 'N',  badgeBg: '#C99A2E', badgeFg: '#2B1810' },
  NH:     { label: 'Not Halal Certified', badge: 'NH', badgeBg: '#C2332F', badgeFg: '#FFFFFF' },
  DO:     { label: 'Dinner Only',         badge: 'DO', badgeBg: '#7E4FB5', badgeFg: '#FFFFFF' },
  MILD:   { label: 'Mild',                icon: 1 },
  MEDIUM: { label: 'Medium',              icon: 2 }
};

export const getTagMeta = (code) => TAG_META[code];

const FlameIcons = ({ count }) => (
  <span className="flex items-center gap-0.5" aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => (
      <Flame key={i} className="w-3.5 h-3.5 text-[#E8869E]" fill="#E8869E" strokeWidth={1.5} />
    ))}
  </span>
);

/**
 * Tag chip: rendered inline on menu item cards.
 * Visual: gold-outlined rounded rectangle wrapping a colored letter badge.
 */
export const TagBadge = ({ code }) => {
  const meta = TAG_META[code];
  if (!meta) return null;
  if (meta.icon) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-lg border border-[#E8B945]/70 bg-[#3A1210] px-2.5 py-1"
        data-testid={`tag-${code}`}
      >
        <FlameIcons count={meta.icon} />
        <span className="text-[#E8D5B7] text-[10px] tracking-wider uppercase font-semibold">
          {meta.label}
        </span>
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center justify-center font-black rounded-lg border-2 border-[#E8B945] shadow-[0_0_8px_rgba(232,185,69,0.3)]"
      style={{
        backgroundColor: meta.badgeBg,
        color: meta.badgeFg,
        minWidth: '2.2rem',
        height: '1.75rem',
        padding: '0 0.45rem',
        fontSize: '0.8rem',
        letterSpacing: '0.03em',
        fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif"
      }}
      data-testid={`tag-${code}`}
    >
      {meta.badge}
    </span>
  );
};

const FilterPill = ({ code, active, onClick }) => {
  const meta = TAG_META[code];
  if (!meta) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full border transition-all py-2 px-4 text-sm ${
        active
          ? 'bg-[#3A1E14] border-[#F5D06E] text-[#F5D06E] shadow-[0_0_0_2px_rgba(245,208,110,0.3)]'
          : 'bg-[#3A1E14]/60 border-[#E8B945]/60 text-[#E8D5B7] hover:border-[#F5D06E] hover:text-[#F5D06E]'
      }`}
      data-testid={`filter-pill-${code}`}
    >
      {meta.icon ? (
        <FlameIcons count={meta.icon} />
      ) : (
        <span
          className="inline-flex items-center justify-center font-bold rounded"
          style={{
            backgroundColor: meta.badgeBg,
            color: meta.badgeFg,
            minWidth: '1.75rem',
            height: '1.25rem',
            padding: '0 0.35rem',
            fontSize: '0.7rem'
          }}
        >
          {meta.badge}
        </span>
      )}
      <span className="tracking-wide">{meta.label}</span>
    </button>
  );
};

const DietaryFilter = ({ activeFilters, onToggle, onClear }) => {
  const codes = Object.keys(TAG_META);

  return (
    <section
      className="mx-4 sm:mx-6 my-10 rounded-2xl border border-[#E8B945]/50 bg-[#3A1E14]/40 backdrop-blur-sm p-6 sm:p-8"
      data-testid="dietary-filter"
    >
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Filter className="w-5 h-5 text-[#F5D06E]" />
          <h3
            className="text-[#F5D06E] font-bold tracking-[0.15em] uppercase text-sm sm:text-base"
            style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
          >
            Filter by Dietary Preferences
          </h3>
        </div>
        {activeFilters.length > 0 && (
          <button
            onClick={onClear}
            className="text-xs text-[#E8D5B7]/70 hover:text-[#F5D06E] underline tracking-wider"
            data-testid="clear-filters-button"
          >
            Clear
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3">
        {codes.map((code) => (
          <FilterPill
            key={code}
            code={code}
            active={activeFilters.includes(code)}
            onClick={() => onToggle(code)}
          />
        ))}
      </div>
    </section>
  );
};

export default DietaryFilter;
